import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  increaseCoachesPage,
  setCoachesPage,
  setHasMoreCoaches,
  setIsLoadingCoaches,
  setIsLoadingLoadMoreBtnCoaches,
} from "../../../../redux/pages/exploreSlice";
import { MaterialCardLoading } from "../cards/MaterialCardLoading";
import { MOCK_ARRAY_10 } from "../../../../lib/constants";
import { CoachesFromApi } from "../../../../interfaces/specialist/CoachesFromApi";
import { getSpecialists } from "../../../../api/coaches";
import { ExploreCoachCard } from "../cards/exploreCoachCard/ExploreCoachCard";
import { ExploreFiltersPage } from "../exploreFilters/ExploreFiltersPage";
import { ExploreCoachesNoResults } from "./ExploreCoachesNoResults";
import { LoadingSpin } from "../../../global/loading/LoadingSpin";

interface ExploreCoachesSectionProps {
  availability?: boolean;
}

export const ExploreCoachesSection = ({
  availability = false,
}: ExploreCoachesSectionProps) => {
  const [coaches, setCoaches] = useState<CoachesFromApi>(null);
  const exploreRedux = useSelector((state: RootState) => state.explore.value);
  const filters = useSelector((state: RootState) => state.filters.coaches);
  const dispatch = useDispatch();
  const observer = useRef<IntersectionObserver>();

  const handleGetMoreCoaches = useCallback(() => {
    getSpecialists(
      filters.queryStringFilter,
      exploreRedux.pages.coaches.page + 1
    )
      .then((resCoaches) => {
        setCoaches({
          count: resCoaches.count,
          rows: [...coaches.rows, ...resCoaches.rows],
        });
        dispatch(setIsLoadingLoadMoreBtnCoaches(false));
        dispatch(increaseCoachesPage());

        if (resCoaches.rows.length === 0) {
          dispatch(setHasMoreCoaches(false));
        }
        return;
      })
      .catch((e: Error) => {
        dispatch(setIsLoadingLoadMoreBtnCoaches(false));

        console.error(e);
      });
  }, [
    dispatch,
    exploreRedux.pages.coaches.page,
    filters.queryStringFilter,
    coaches?.rows,
  ]);

  const lastCoachRef = useCallback(
    (node: HTMLDivElement) => {
      if (exploreRedux.pages.coaches.isLoadingLoadMoreBtn) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && exploreRedux.pages.coaches.hasMore) {
          dispatch(setIsLoadingLoadMoreBtnCoaches(true));

          void handleGetMoreCoaches();
        }
      });

      if (node) observer.current.observe(node);
    },
    [
      exploreRedux.pages.coaches.isLoadingLoadMoreBtn,
      exploreRedux.pages.coaches.hasMore,
      handleGetMoreCoaches,
      dispatch,
    ]
  );

  useEffect(() => {
    dispatch(setIsLoadingCoaches(true));
    dispatch(setHasMoreCoaches(true));
    dispatch(setCoachesPage(0));

    const queryStringForGetSpecialists = availability
      ? filters.queryStringFilter + "&availability=true"
      : filters.queryStringFilter;

    getSpecialists(queryStringForGetSpecialists, 0)
      .then((resCoaches) => {
        setCoaches(resCoaches);
        dispatch(setIsLoadingCoaches(false));
      })
      .catch((e: Error) => {
        console.error(e);
      });
  }, [dispatch, filters.queryStringFilter, availability]);

  return (
    <>
      <ExploreFiltersPage filterFor="coaches" type="sports" />
      <div className="w-full grid grid-col-1 sm:grid-cols-2 md:grid-cols-3 gap-3  md:gap-x-4 gap-y-6 lg:gap-6 ">
        {!exploreRedux.pages.coaches.isLoadingItems
          ? coaches?.rows.map((coach, i) => {
              if (i + 1 === coaches.rows.length) {
                return (
                  <div key={i} ref={lastCoachRef}>
                    <ExploreCoachCard data={coach} />
                  </div>
                );
              }
              return <ExploreCoachCard key={i} data={coach} />;
            })
          : MOCK_ARRAY_10.map((item) => {
              return <MaterialCardLoading key={item} />;
            })}
      </div>

      {exploreRedux.pages.coaches.isLoadingLoadMoreBtn && <LoadingSpin />}

      {coaches?.rows.length === 0 &&
        !exploreRedux.pages.coaches.isLoadingItems && (
          <ExploreCoachesNoResults />
        )}
    </>
  );
};
